/**
 * SIGNAL INTRUSIONS — a second voice in the feed. Not the host: a previous
 * Participant still inside the network. The ARCHIVE (src/story/archive.ts)
 * establishes who: Participant 002 ran cable for six years, enlisted against
 * a debt (record 7), is filed "concluded, floor 9" on the roster (record 12),
 * was recognized on the feed by a viewer (record 16) — and the unnumbered
 * handwritten page (record 18, filed under RECURRING) is in the same hand.
 *
 * Register: human, terse, lowercase — someone typing fast from inside the
 * walls. Deadpan-world-consistent: no exclamation points (permanent
 * constraint), no melodrama; the urgency is in what is said, not how.
 * Signature themes: keep moving, the draw, the cable runs, the turnstile,
 * being filed concluded while still typing.
 *
 * Rendered on the feed's 'sig' channel (SIG·### prefix, violet accent).
 * Triggering lives in Game: a low-probability roll on floor transitions past
 * act 1 (tuning.story.intrusionChance, level RNG stream) plus a guaranteed
 * cut-in when a Participant-related record is recovered. Never more than one
 * intrusion per floor.
 */

/** generic cut-ins: rolled on eligible floor transitions (act 2+) */
export const INTRUSION_FLOOR: readonly string[] = [
  'still here. the roster says concluded. the roster is a formatting choice.',
  'i ran the cable behind these walls. i know where the feed goes. it does not go up.',
  'don’t stand still. that isn’t advice. standing still is how they anchor the floors.',
  'you’re on the feed. i’m in it now. there is a difference. keep it that way.',
  'floor nine is where they logged me out. i kept going. logging is not the same as leaving.',
  'the exit signs are set dressing. the turnstile is real. keep moving.',
  'eat everything you find. the draw doesn’t skip meals. neither should you.'
]

/**
 * Guaranteed cut-ins keyed to the Participant-related archive records —
 * DECISION: 7 (002's intake form), 12 (the roster line that files 002
 * concluded), 16 (the viewer letter that recognized the cable runner). The
 * voice reacts to the specific document the participant just picked up.
 */
export const INTRUSION_RECORDS: Readonly<Partial<Record<number, string>>> = {
  7: 'that form you’re holding. i wrote it. nine was optimistic. so was the countersignature.',
  12: 'concluded, floor 9. that line is about me. i am still typing. draw your own conclusion.',
  16: 'someone wrote in about me. it was him, tell them. tell them he kept moving.'
}
